import api from "../api/axios";

export interface LoginPayload {
  email: string;
  password: string;
  applicationType?: string;
}

export interface LoginResponse {
  status: boolean;
  message: string;
  token: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
  };
}

const authService = {
  /**
   * Calls the existing API gateway login endpoint.
   * Mirrors the payload shape used by prx_buyerapp-ui (`authService.login`).
   */
  login: async (payload: LoginPayload): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>("/auth/login", {
      ...payload,
      applicationType: payload.applicationType ?? "Shell",
    });
    return data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Best-effort — even if the backend call fails we still clear locally.
    }
  },

  /**
   * Requests a password-reset email for a registered user.
   * Mirrors prx_buyerapp-ui `authService.reSetPassword` →
   * POST /users/sendResetEmailToRegisteredUser.
   */
  requestPasswordReset: async (payload: {
    email: string;
  }): Promise<{ status: boolean; message?: string }> => {
    const { data } = await api.post(
      "/users/sendResetEmailToRegisteredUser",
      payload
    );
    return data;
  },

  /**
   * Sets a new password from the reset-email link.
   * Mirrors prx_buyerapp-ui `authService.setPassword` →
   * PUT /users/editPassword. The reset token (from the link's `data` param)
   * must already be stored in the access-token cookie so the axios
   * interceptor sends it as the Authorization header.
   */
  setPassword: async (payload: {
    id: number;
    password: string;
  }): Promise<{ status: boolean; message?: string }> => {
    const { data } = await api.put("/users/editPassword", payload);
    return data;
  },
};

export default authService;
